'use client';

import { useSessionManager } from '@/hooks/useSessionManager';
import { useSessionControl } from '@/hooks/useSessionControl';
import { useTcaSessionBridge } from '@/hooks/useTcaSessionBridge';
import { authService } from '@/lib/authService';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import PortalLockScreen from './PortalLockScreen';
import { RemoteSupportReceiver } from './RemoteSupportReceiver';
import { wsClient } from '@/lib/websocket/TotalAccessWebSocket';

interface SessionContextType {
  sessionState: {
    isActive: boolean;
    lastActivity: number;
    timeUntilTimeout: number;
    showWarning: boolean;
    warningCountdown: number;
    isLocked: boolean;
  };
  extendSession: () => void;
  flushOfflinePermissions: () => Promise<void>;
  lockSession: () => void;
  unlockSession: () => Promise<void>;
  logout: () => void;
  config: {
    timeoutMinutes: number;
    warningSeconds: number;
    checkIntervalSeconds: number;
  };
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const sessionManager = useSessionManager();
  const wasLockedRef = useRef(false);

  // Phase 11: subscribe to session-control WebSocket channel so the portal
  // can push lock_now / sign_out_everywhere / session_terminated / stay_alive
  // commands to all connected browser tabs. The hook is gated on
  // shouldMonitorSession so it only subscribes for authenticated, monitored
  // sessions (not on auth routes or public pages).
  const currentUserId = authService.getUser()?.id ?? null;
  useSessionControl({
    userId: currentUserId,
    enabled: sessionManager.sessionState.isActive,
  });

  // Phase 12: TCD Session Bridge — on TCD-managed Windows devices, bridge
  // OS-level lock/unlock/activity events to the portal session. When the OS
  // is locked, the portal locks too. When OS activity is detected, the
  // inactivity timer is reset (stay-alive). The hook auto-detects TCD via
  // the bridge health endpoint and is a no-op when TCD is not present.
  // Phase 12: policy gates come from the user's security_policy (client-scoped
  // device_policies resolved in /api/v1/auth/me); absent rows default true.
  const sessionPolicies = authService.getUser()?.security_policy?.sessionPolicies;

  // Only probe the local agent bridge when the signed-in user actually has
  // TCD assigned (has_tcd_binding from /auth/me). Without this every
  // authenticated Windows browser polls a closed 127.0.0.1:9797 and logs
  // ERR_CONNECTION_REFUSED in devtools. authStateChanged re-fires whenever
  // /auth/me refreshes the stored user, so the flag is picked up as soon as
  // it lands even if it wasn't present at login.
  const [tcdAssigned, setTcdAssigned] = useState(false);
  useEffect(() => {
    const refresh = () => setTcdAssigned(authService.getUser()?.has_tcd_binding === true);
    refresh();
    authService.addEventListener('authStateChanged', refresh);
    return () => authService.removeEventListener('authStateChanged', refresh);
  }, []);

  useTcaSessionBridge({
    enabled: sessionManager.sessionState.isActive && tcdAssigned,
    lockOnOsLock: sessionPolicies?.portalLockOnOsLock !== false,
    stayAliveOnOsActivity: sessionPolicies?.portalStayAliveOnOsActivity !== false,
  });

  // Phase 10.3: Remote Support Receiver — listens for support_session_started
  // WebSocket events and shows a consent dialog. If the user accepts, captures
  // the screen via getDisplayMedia and publishes to the LiveKit support room.
  // This is the browser-based path (view-only). Agent-based sessions are
  // handled natively by the TCD agent.
  const supportUserId = authService.getUser()?.id ?? null;
  // RemoteSupportReceiver is rendered below — it uses useSupportSession internally

  // Never show the lock screen on auth routes — those are authentication
  // processing pages (login, oauth-success, mfa, etc.), not the locked portal.
  // Without this, the lock screen overlays /auth/oauth-success for a few
  // seconds after SSO re-authentication, because isLocked is still true in
  // React state until the full page reload re-initialises useSessionManager.
  const isAuthRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth/');
  const shouldShowLockScreen = sessionManager.sessionState.isLocked && authService.isAuthenticated() && !isAuthRoute;

  // Disconnect WebSocket when the lock screen is shown, and reconnect when
  // unlocked. While locked, the session tokens stop refreshing, so the
  // WebSocket token becomes stale and causes 502 handshake errors.
  // Gated on shouldShowLockScreen so we don't disconnect on auth routes.
  useEffect(() => {
    if (shouldShowLockScreen && !wasLockedRef.current) {
      console.log('[SessionProvider] Lock screen shown — disconnecting WebSocket');
      wsClient.disconnect(true);
      wasLockedRef.current = true;
    } else if (!shouldShowLockScreen && wasLockedRef.current) {
      console.log('[SessionProvider] Lock screen dismissed — reconnecting WebSocket');
      wasLockedRef.current = false;
      // Reconnect only if still authenticated
      if (authService.isAuthenticated()) {
        wsClient.connect().catch((err: unknown) => {
          console.warn('[SessionProvider] WebSocket reconnect after unlock failed:', err);
        });
      }
    }
  }, [shouldShowLockScreen]);

  // Phase 13: Listen for sessionTerminated events dispatched by the
  // Capacitor push handler (capacitorDeviceService) or the PWA messaging
  // service (messagingService). These are fired when the server sends a
  // `session_terminated` or `account_disabled` FCM data-only push. The
  // push handler already calls authService.clearAuth() — this listener
  // handles the redirect to login and WebSocket cleanup.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleSessionTerminated = (event: Event) => {
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      console.log('[SessionProvider] Session terminated by server:', detail?.reason);
      // Disconnect WebSocket immediately
      wsClient.disconnect(true);
      // authService.clearAuth() is already called by the push handler,
      // but call it again as a safety net in case the event was dispatched
      // from a code path that didn't clear auth (e.g., SW postMessage).
      authService.clearAuth();
      // Redirect to login with the termination reason
      const reason = detail?.reason || 'session_terminated';
      window.location.href = `/auth/login?reason=${encodeURIComponent(reason)}`;
    };

    window.addEventListener('sessionTerminated', handleSessionTerminated as EventListener);
    return () => {
      window.removeEventListener('sessionTerminated', handleSessionTerminated as EventListener);
    };
  }, []);

  return (
    <SessionContext.Provider value={sessionManager}>
      {/* `inert` while locked: the overlay alone blocks pointer events but not
          keyboard — a previously focused input behind it would still receive
          keystrokes and Enter-submit. inert removes the app from the tab order
          and blocks all interaction/focus while the lock screen is shown.
          display:contents keeps the wrapper layout-transparent. */}
      <div inert={shouldShowLockScreen || undefined} style={{ display: 'contents' }}>
        {children}
      </div>
      {/* Lock screen overlay - shown when session is locked due to inactivity
          Only render if session is actually locked AND user has valid auth tokens.
          Without the auth check, the lock screen can overlay /auth/login when
          the session has expired, making it impossible to log in. */}
      {shouldShowLockScreen && (
        <PortalLockScreen 
          onUnlock={sessionManager.unlockSession}
          onLogout={sessionManager.logout}
        />
      )}
      {/* Inactivity warning - shown 30 seconds before lock */}
      {sessionManager.sessionState.showWarning && !sessionManager.sessionState.isLocked && (
        <SessionTimeoutWarning
          countdown={sessionManager.sessionState.warningCountdown}
          onExtend={sessionManager.extendSession}
          onLogout={sessionManager.logout}
        />
      )}
      {/* Phase 10.3: Remote Support Receiver — browser-based screen share consent */}
      {!shouldShowLockScreen && (
        <RemoteSupportReceiver
          userId={supportUserId}
          enabled={sessionManager.sessionState.isActive}
        />
      )}
    </SessionContext.Provider>
  );
}

interface SessionTimeoutWarningProps {
  countdown: number;
  onExtend: () => void;
  onLogout: () => void;
}

function SessionTimeoutWarning({ countdown, onExtend, onLogout }: SessionTimeoutWarningProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <div className="text-center">
          <div className="mb-4">
            <i className="fas fa-clock text-4xl text-orange-500 mb-2"></i>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Session Idle Warning</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Your portal will lock in <span className="font-bold text-red-600">{countdown}</span> seconds due to inactivity.
          </p>
          <div className="flex space-x-3 justify-center">
            <button
              onClick={onExtend}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              I&apos;m Still Here
            </button>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
